/* NVRSD — welcome popup with scratch card. */
if (!customElements.get('nvrsd-welcome')) {
  const STORE_KEY = 'nvrsd-welcome-v1'; // "closed" | "submitted" — never show again on this device
  const PENDING_KEY = 'nvrsd-welcome-pending'; // set when we fall back to a full-page form submit

  const store = {
    get(key, session) {
      try {
        return (session ? sessionStorage : localStorage).getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value, session) {
      try {
        (session ? sessionStorage : localStorage).setItem(key, value);
      } catch (e) {}
    },
    remove(key, session) {
      try {
        (session ? sessionStorage : localStorage).removeItem(key);
      } catch (e) {}
    },
  };

  customElements.define(
    'nvrsd-welcome',
    class NvrsdWelcome extends HTMLElement {
      connectedCallback() {
        this.dialog = this.querySelector('[role="dialog"]');
        this.form = this.querySelector('form');
        this.canvas = this.querySelector('[data-scratch]');
        this.codeDisplay = this.querySelector('[data-code-display]');
        this.code = this.dataset.code;

        this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
        this.querySelector('[data-reveal-btn]').addEventListener('click', () => this.reveal());
        this.querySelector('[data-copy]').addEventListener('click', () => this.copy());
        this.form.addEventListener('submit', (event) => this.submit(event));
        this.onKeydown = (event) => {
          if (event.key === 'Escape') this.close();
          if (event.key === 'Tab') this.trapFocus(event);
        };

        // Returning from a full-page fallback submit
        const posted = this.form.querySelector('[data-posted]');
        if (posted && posted.dataset.posted === 'true' && store.get(PENDING_KEY, true)) {
          store.remove(PENDING_KEY, true);
          this.open();
          this.reveal(true);
          this.showSuccess();
          return;
        }

        if (store.get(STORE_KEY)) return;
        if (window.Shopify && window.Shopify.designMode) return;

        const delay = Math.max(0, Number(this.dataset.delay || 5)) * 1000;
        this.timer = setTimeout(() => this.openWhenClear(), delay);
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        document.removeEventListener('keydown', this.onKeydown);
      }

      // Shopify's cookie banner sits above everything (and over our close button on mobile):
      // wait until the visitor has dealt with it before opening.
      openWhenClear() {
        const banner = document.getElementById('shopify-pc__banner');
        const visible = banner && banner.getClientRects().length > 0 && getComputedStyle(banner).visibility !== 'hidden';
        if (visible) {
          this.timer = setTimeout(() => this.openWhenClear(), 1200);
          return;
        }
        this.open();
      }

      open() {
        this.previousFocus = document.activeElement;
        this.hidden = false;
        document.documentElement.classList.add('nvrsd-welcome-open');
        requestAnimationFrame(() => {
          this.classList.add('is-open');
          this.setupScratch();
          this.dialog.focus({ preventScroll: true });
        });
        document.addEventListener('keydown', this.onKeydown);
      }

      close() {
        if (!this.classList.contains('is-done')) store.set(STORE_KEY, 'closed');
        this.classList.remove('is-open');
        document.documentElement.classList.remove('nvrsd-welcome-open');
        document.removeEventListener('keydown', this.onKeydown);
        setTimeout(() => (this.hidden = true), 350);
        if (this.previousFocus && this.previousFocus.focus) this.previousFocus.focus({ preventScroll: true });
      }

      trapFocus(event) {
        const items = [...this.dialog.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])')].filter(
          (el) => !el.hidden && el.offsetParent !== null
        );
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      /* ---------- Scratch card ---------- */

      setupScratch() {
        if (this.scratchReady || this.classList.contains('is-scratched')) return;
        const canvas = this.canvas;
        const rect = canvas.getBoundingClientRect();
        if (!rect.width) return;
        const ratio = window.devicePixelRatio || 1;
        canvas.width = Math.round(rect.width * ratio);
        canvas.height = Math.round(rect.height * ratio);
        const ctx = canvas.getContext('2d');
        ctx.scale(ratio, ratio);

        // Cover: burgundy with a fine diagonal sheen and a label
        const gradient = ctx.createLinearGradient(0, 0, rect.width, rect.height);
        gradient.addColorStop(0, '#8a2a2a');
        gradient.addColorStop(0.5, '#7b2020');
        gradient.addColorStop(1, '#5e1717');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, rect.width, rect.height);
        ctx.strokeStyle = 'rgba(250, 248, 245, 0.07)';
        ctx.lineWidth = 1;
        for (let x = -rect.height; x < rect.width; x += 8) {
          ctx.beginPath();
          ctx.moveTo(x, rect.height);
          ctx.lineTo(x + rect.height, 0);
          ctx.stroke();
        }
        const font = getComputedStyle(document.body).fontFamily;
        ctx.fillStyle = 'rgba(250, 248, 245, 0.92)';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = `500 12px ${font}`;
        ctx.letterSpacing = '3px';
        ctx.fillText('SCRATCH HERE', rect.width / 2, rect.height / 2);

        this.ctx = ctx;
        this.rect = rect;
        this.brush = Math.max(22, rect.width / 12);
        this.scratching = false;

        canvas.addEventListener('pointerdown', (e) => {
          this.scratching = true;
          this.classList.add('is-ready'); // prize renders only once scratching starts
          canvas.setPointerCapture(e.pointerId);
          this.scratchAt(e);
        });
        canvas.addEventListener('pointermove', (e) => this.scratching && this.scratchAt(e));
        const stop = () => {
          if (!this.scratching) return;
          this.scratching = false;
          this.last = null;
          if (this.clearedRatio() > 0.45) this.reveal();
        };
        canvas.addEventListener('pointerup', stop);
        canvas.addEventListener('pointercancel', stop);
        this.scratchReady = true;
      }

      scratchAt(event) {
        const r = this.canvas.getBoundingClientRect();
        const x = event.clientX - r.left;
        const y = event.clientY - r.top;
        const ctx = this.ctx;
        ctx.globalCompositeOperation = 'destination-out';
        ctx.strokeStyle = '#000'; // full-strength eraser (the cover drawing left a translucent stroke style)
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = this.brush;
        ctx.beginPath();
        if (this.last) ctx.moveTo(this.last.x, this.last.y);
        else ctx.moveTo(x - 0.1, y);
        ctx.lineTo(x, y);
        ctx.stroke();
        this.last = { x, y };
        if (!this.hintDone) {
          this.hintDone = true;
          this.classList.add('is-scratching');
        }
      }

      clearedRatio() {
        const { width, height } = this.canvas;
        const data = this.ctx.getImageData(0, 0, width, height).data;
        let clear = 0;
        let total = 0;
        for (let i = 3; i < data.length; i += 4 * 24) {
          total++;
          if (data[i] < 64) clear++;
        }
        return total ? clear / total : 0;
      }

      reveal(instant) {
        if (this.classList.contains('is-scratched')) return;
        this.classList.add('is-scratched', 'is-ready');
        if (instant) this.classList.add('is-instant');
        this.querySelector('[data-face]').removeAttribute('aria-hidden');
        const hint = this.querySelector('[data-hint]');
        if (hint) hint.hidden = true;
        this.querySelector('[data-step2]').hidden = false;
      }

      /* ---------- Email ---------- */

      async submit(event) {
        event.preventDefault();
        const error = this.querySelector('[data-error]');
        error.hidden = true;
        if (!this.form.reportValidity()) return;
        this.reveal();

        const button = this.querySelector('[data-submit]');
        button.disabled = true;
        button.classList.add('is-loading');

        try {
          const response = await fetch(this.form.action, {
            method: 'POST',
            body: new FormData(this.form),
            headers: { Accept: 'text/html' },
          });
          if (/\/challenge/.test(response.url)) return this.fallbackSubmit();
          if (response.ok && /customer_posted=true/.test(response.url)) return this.showSuccess();

          const html = await response.text();
          const message = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('#NvrsdWelcomeForm .form__message, #NvrsdWelcomeForm .errors');
          error.textContent = (message && message.textContent.trim()) || 'Please check your email address and try again.';
          error.hidden = false;
        } catch (e) {
          return this.fallbackSubmit();
        } finally {
          button.disabled = false;
          button.classList.remove('is-loading');
        }
      }

      fallbackSubmit() {
        // Shopify may ask for a captcha; let the browser do a normal submit and reopen here afterwards.
        store.set(PENDING_KEY, '1', true);
        HTMLFormElement.prototype.submit.call(this.form);
      }

      showSuccess() {
        store.set(STORE_KEY, 'submitted');
        this.classList.add('is-done');
        this.codeDisplay.textContent = this.code;
        this.codeDisplay.hidden = false;
        const sub = this.querySelector('[data-card-sub]');
        if (sub) sub.hidden = true;
        this.querySelector('[data-step2]').hidden = true;
        this.querySelector('[data-done]').hidden = false;
        this.querySelector('[data-copy]').hidden = false;
        this.applyToCart();
      }

      // Put the code on the cart straight away so it shows in the cart and carries into checkout.
      async applyToCart() {
        try {
          const cart = await fetch('/cart.js').then((r) => r.json());
          const codes = (cart.discount_codes || []).map((d) => d.code.toUpperCase()).filter((c) => c !== this.code);
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: [...codes, this.code].join(',') }),
          });
        } catch (e) {}
      }

      async copy() {
        const button = this.querySelector('[data-copy]');
        try {
          await navigator.clipboard.writeText(this.code);
          button.textContent = 'Copied';
        } catch (e) {
          const range = document.createRange();
          range.selectNodeContents(this.codeDisplay);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          button.textContent = 'Selected, press copy';
        }
        setTimeout(() => (button.textContent = 'Copy code'), 2000);
      }
    }
  );
}
